import { useLocation } from 'react-router-dom';
import { Card, Badge, Button, EngineKeyline } from '@/components/ui/primitives';
import { InstrumentBezelIcon } from '@/components/identity';
import { engineForPath, MARKETPLACE, type Engine } from '@/engines/registry';

/* Shared "coming / reserved" preview (brief §5.4). Non-GA engines and the
 * Marketplace route here. The shell is identical for everyone — a locked engine
 * previews its value + one restrained roadmap CTA, never a raw 403. */

interface PreviewData {
  key: string;
  name: string;
  status: string;
  tagline: string;
  description: string;
  capabilities: { key: string; label: string }[];
}

function toPreview(engine: Engine): PreviewData {
  return {
    key: engine.key,
    name: engine.name,
    status: engine.status,
    tagline: engine.tagline,
    description: engine.description,
    capabilities: engine.capabilities,
  };
}

export function EnginePreview() {
  const location = useLocation();
  const engine = engineForPath(location.pathname);

  const data: PreviewData = engine
    ? toPreview(engine)
    : {
        key: MARKETPLACE.key,
        name: MARKETPLACE.name,
        status: MARKETPLACE.status,
        tagline: MARKETPLACE.tagline,
        description: MARKETPLACE.description,
        capabilities: [],
      };

  const reserved = data.status === 'reserved';

  return (
    <div data-engine={data.key} className="mx-auto max-w-2xl py-10">
      <Card className="overflow-hidden">
        <EngineKeyline />
        <div className="p-8 text-center">
          <div className="mx-auto mb-4 inline-flex rounded-lg bg-engine-wash p-3">
            <InstrumentBezelIcon glyph={engine?.icon ?? MARKETPLACE.icon} active size={40} />
          </div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-engine">
            {reserved ? 'Reserved · BookedCentral' : 'Coming · BookedCentral'}
          </div>
          <div className="mt-1.5 flex items-center justify-center gap-2">
            <h1 className="font-display text-display-md font-semibold tracking-tight text-text-display">{data.name}</h1>
            <Badge tone={reserved ? 'neutral' : 'engine'}>{reserved ? 'Reserved' : 'Coming soon'}</Badge>
          </div>
          <p className="mt-2 text-body-lg font-medium text-engine">{data.tagline}</p>
          <p className="mx-auto mt-3 max-w-md text-body-md text-text-secondary">{data.description}</p>

          {data.capabilities.length > 0 && (
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {data.capabilities.map((c) => (
                <Badge key={c.key} tone="neutral">{c.label}</Badge>
              ))}
            </div>
          )}

          <div className="mt-8">
            {/* not an AI affordance — copper is reserved for the machine (§2.1.1) */}
            <Button
              variant="primary"
              disabled
              aria-disabled="true"
              title="Not available yet - there is no sign-up to wire this to. It lights up in the same shell when it launches."
            >
              {reserved ? 'Register interest' : 'Notify me when it launches'}
            </Button>
          </div>
          <p className="mt-4 text-[11px] text-text-tertiary">
            Part of your BookedCentral OS — it plugs into the same shell, data model and copilot when it
            lights up. Nothing here restructures.
          </p>
        </div>
      </Card>
    </div>
  );
}
